import type { JSX } from "react";
import type { ErrorDetails } from "../../../services/error/error-service";
import { Button, css, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, styled, Typography } from "@mui/material";
import { useState } from "react";
import { ErrorService } from "../../../services/error/error-service";
import { AdminIcon, CopyIcon, SuccessIcon } from "../../atoms/icons/mui-icons";
import { CentredSpinner } from "../../atoms/centred-spinner";


// NOTE: Do not assume that we are rendering within the usual service context!
// This Component will be rendered in isolation if an error is thrown during
// the app initialisation, so `useServices()`, or any other hook which depends
// on it should not be used.

const getContent = (temporarilyUnavailable: boolean): { title: string, message: string } => {
	if(temporarilyUnavailable) {
		return {
			title: "Service Temporarily Unavailable",
			message: "We are currently unable to process your request. Please try again shortly.",
		};
	}

	return {
		title: "Something went wrong",
		message: "Our team have been notified. If the issue persists, please contact our support.",
	};
};

type ErrorDialogProps = {
	 error: unknown;
	 details?: ErrorDetails | null;
	 admin?: boolean;
	 onClickOk: VoidFunction;
};

const DETAILS_HEIGHT = "300px";
const DetailsView = styled("pre")(({ theme }) => css`
	background-color: ${theme.palette.grey[100]};
	overflow: auto;
	height: ${DETAILS_HEIGHT};
	max-height: ${DETAILS_HEIGHT};
	padding: ${theme.spacing(1)};
	font-size: .8rem;
	box-shadow: inset ${theme.shadows[1]};
	margin: 0;
	border-bottom-left-radius: ${theme.shape.borderRadius}px;
	border-bottom-right-radius: ${theme.shape.borderRadius}px;
`);

const ErrorDetailsView = (p: ErrorDialogProps): JSX.Element | null => {
	const [ copied, setCopied ] = useState(false);

	if(!p.admin) {
		return null;
	}

	const handleCopyClick = () => {
		if(!p.details) {
			return;
		}

		setCopied(false);
		const message = "```\n" +JSON.stringify(p.details, null, 2) + "\n```";
		window.navigator.clipboard.writeText(message)
			.then(() => {
				setCopied(true);
				setTimeout(() => {
					setCopied(false);
				}, 2500);
			});
	};

	return (
		<Paper variant="outlined" sx={{ marginTop: 2 }}>
			<Stack direction="row" spacing={ 1 } sx={{ padding: 1, alignItems: "center" }}>
				<AdminIcon color="error"/>
				<Typography component="h2" variant="h5">Error Details</Typography>
				<Button
					size="small"
					variant="contained"
					sx={{ marginLeft: "auto" }}
					startIcon={ copied ? <SuccessIcon/> : <CopyIcon/> }
					disabled={ !p.details }
					color={ copied ? "success" : "primary" }
					onClick={ handleCopyClick }
				>
					{ copied ? "Copied!" : "Copy" }
				</Button>
			</Stack>

			<DetailsView>
				{ !p.details && <CentredSpinner/> }
				{ p.details && JSON.stringify(p.details, null, 2) }
			</DetailsView>
		</Paper>
	);
};

export const ErrorDialog = (p: ErrorDialogProps): JSX.Element | null => {
	if(!p.error) {
		return null;
	}

	const temporarilyUnavailable = ErrorService.isUpstream503Error(p.error);
	const { title, message } = getContent(temporarilyUnavailable);

	return (
		<Dialog open>
			<DialogTitle>{ title }</DialogTitle>
			<DialogContent>
				<Stack>
					<div>{ message }</div>
					{ !temporarilyUnavailable && (
						<ErrorDetailsView { ...p }/>
					) }
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={ p.onClickOk } variant="contained">
					OK
				</Button>
			</DialogActions>
		</Dialog>
	);
};
